/**
 * Analytics Database (IndexedDB Wrapper)
 *
 * Provides persistent storage for analytics events using IndexedDB.
 * Events are stored locally and synced to the server when online.
 *
 * ## Por que a leitura NÃO usa o índice `pending_sync` (Renato 2026-09-21)
 *
 * No Chromium, `IDBObjectStore.delete()` **não remove as entradas de índice**
 * do registro: `BackingStore::Transaction::DeleteRange` apaga os dados do
 * object store e as `ExistsEntryKeys`, e nada mais. A entrada de índice vira
 * órfã e fica.
 *
 * Um cursor de ÍNDICE paga um `Get()` de ponto por entrada órfã antes de
 * descobrir que ela está morta (`IndexCursorImpl::LoadCurrentRow` → `Get` →
 * `RemoveTombstoneOrIncrementCount`), num laço sem early-exit. A ~15–30 µs por
 * `Get()` num eMMC de tablet, isso escala linearmente com o total de eventos
 * JÁ SINCRONIZADOS na vida do device.
 *
 * Medido em produção, três pontos independentes, todos dentro de fator 2 do
 * modelo "nº de entradas mortas × ~31 µs":
 *
 *   sonda limpa (0 mortas) .................... 2 ms
 *   banco novo (~12 mil mortas) ............. 376 ms
 *   banco de 5 meses (~210 mil mortas) .... 6.509 ms
 *
 * Pior ainda: `only(1)` consulta exatamente a faixa onde 100% das lápides
 * caem, porque `1` é o valor que some quando o registro é sincronizado. E o
 * caso "fila vazia" é o mais caro de todos — sem entrada viva, o cursor varre
 * a faixa inteira antes de concluir que não há nada.
 *
 * O tombstone sweeper do Chromium limparia isso, mas roda como *pre-close
 * task*: exige que a última conexão IndexedDB da origem feche e passem 2 s
 * (`kBackingStoreGracePeriod`). Um player de signage mantém a conexão aberta
 * 24/7 — `CanClose()` nunca fica verdadeiro e o sweeper nunca roda.
 *
 * A solução é não usar o índice: como o evento é DELETADO após o ack
 * (`deleteSynced`), todo registro no store já é pendente por definição — o
 * índice não filtra nada. O cursor de OBJECT STORE lê o valor direto do
 * iterador, sem `Get()` extra, e registros deletados nem aparecem (o LevelDB
 * limpa esses por compactação automática, que roda continuamente).
 *
 * ⚠️ Não reintroduzir consulta por `index('pending_sync')` no caminho quente.
 * O índice segue declarado só para não exigir um `versionchange`; está inerte.
 */

import { AnalyticsEvent, IAnalyticsDB } from './types';
import { logger } from '../logging/logger';

const DB_NAME = 'efflux_analytics';
// v2 (2026-07-05): índice `pending_sync` (0/1) pra consultar pendentes SEM
// getAll() do store inteiro. `synced` é boolean → key inválida em IndexedDB
// (índice inútil), por isso getUnsyncedEvents fazia getAll()
// + filtro em JS — o que travava a main-thread quando o store crescia (165k).
const DB_VERSION = 2;
const STORE_NAME = 'events';

// Flag (localStorage) que marca que a manutenção one-time v2 já rodou até o fim,
// pra não re-escanear o store inteiro a cada boot.
/**
 * A flag é VERSIONADA de propósito: subir o sufixo faz a limpeza one-shot rodar
 * mais uma vez na frota inteira.
 *
 * v3 (Renato 2026-09-20): a partir de agora o flush APAGA o que o servidor
 * confirma (vide `deleteSynced`), então sincronizado não acumula mais. Mas os
 * devices que já rodaram a v2 carregam o estoque morto de antes — no Tab A7
 * eram 77 mil registros sincronizados pra 442 pendentes, inflando o IndexedDB
 * a 433 MB (o dado real cabe em ~26 MB). Esta passada limpa esse estoque uma
 * vez; depois dela o banco se mantém sozinho.
 */
const MAINT_FLAG_KEY = 'efflux_analytics_maint_v3';
/**
 * A limpeza única apaga TODO registro já sincronizado, sem janela de tempo.
 *
 * Havia aqui uma janela de 24h, documentada como "prova de exposição fica 24h
 * após ack". Ela foi APOSENTADA pelo Renato em 20/09/2026, junto com a regra
 * dos 7 dias: "depois que sincronizou, não faz mais nada com o dado". O
 * `deleteSynced` já apaga no ack desde então — manter 24h aqui deixava as
 * duas políticas se contradizendo, e cada tablet carregando ~11 mil registros
 * mortos para sempre, que ainda ocupavam espaço no teto de MAX_STORED_EVENTS.
 */
const MAINT_CHUNK = 500;

/**
 * Teto do acúmulo offline: QUANTIDADE, não idade (Renato 2026-09-20).
 *
 * Quando o total passa de MAX_STORED_EVENTS, os MAIS ANTIGOS são descartados
 * até caber. Não há mais regra por idade.
 *
 * A regra anterior era "guarda 7 dias e depois elimina" — enunciada pelo
 * próprio Renato e APOSENTADA por ele em 20/09/2026, depois que a pesquisa de
 * mercado mostrou que nenhum concorrente limita por dias: todos que publicam
 * número limitam por CONTAGEM de eventos (OnSign TV 100.000, Broadsign
 * 300.000, Play Digital Signage 10.000). Dias no player não protegem contra o
 * que importa — disco e volume — porque um player parado gera pouco e um
 * player ativo gera muito, no mesmo intervalo.
 *
 * Contagem também é a única métrica que funciona com relógio errado. Idade
 * depende do relógio do device: se ele estiver adiantado, nada nunca vence.
 *
 * Descarta o MAIS ANTIGO primeiro porque, quando o player volta do offline,
 * é o dado recente que ainda tem valor operacional. O cursor anda pelo índice
 * `timestamp`, então o legado já sincronizado (mais velho) sai naturalmente
 * antes de qualquer pendente.
 *
 * ESTE É O ÚNICO PONTO DO PLAYER QUE APAGA EVENTO NÃO SINCRONIZADO.
 *
 * NÃO existe alerta de "acúmulo alto": se acumulou é porque não há servidor
 * pra avisar; quando há servidor, não acumula. O que o método devolve é um
 * RECIBO do descarte — a janela perdida — que o AnalyticsService reporta
 * assim que a conexão volta. É a única informação aqui que o servidor não
 * deduz sozinho do `last_seen`.
 */
const MAX_STORED_EVENTS = 50_000;
const RETENTION_SCAN_CAP = 5000;

/**
 * Teto de registros que `getUnsyncedEvents` varre procurando pendentes.
 *
 * Só morde em device que ainda tenha muito registro com `pending_sync=0`
 * (sincronizado numa versão antiga, antes do `deleteSynced`, e ainda não
 * removido pela manutenção one-shot). Varrer o object store é barato — ~1 µs
 * por registro, sem `Get()` — mas o caminho é quente, então tem teto.
 */
const SCAN_CAP = 20_000;

/**
 * Opções de transação de ESCRITA (Renato 2026-09-20).
 *
 * `durability: 'relaxed'` dá o commit assim que o dado chega ao buffer do SO,
 * sem esperar o fsync. Medido por terceiros em escritas pequenas: 1.074 ms →
 * 65 ms (~16×) — e o nosso padrão é exatamente esse, uma transação por evento.
 *
 * TRADE-OFF, explícito: queda ABRUPTA de energia pode perder os últimos
 * segundos de eventos que ainda estavam no buffer. NÃO há risco de corrupção —
 * muda só QUANDO o flush acontece, não a integridade.
 *
 * Por que é aceitável: desde o Chrome 121 `relaxed` já é o PADRÃO (e Safari e
 * Firefox sempre foram assim), então em WebView moderno isto não muda nada —
 * é o comportamento que a frota já tem. O ganho aparece nos motores ANTIGOS
 * (WebView 83–120 e Tizen 6.5+/M85), onde o padrão ainda é estrito; é lá
 * também que o trade-off passa a valer.
 *
 * Só em transações de ESCRITA. Leitura não tem durabilidade a relaxar.
 *
 * Detecção por capacidade, não por versão: abaixo do Chrome 83 (Tizen ≤ 6.0 =
 * M76) a opção não existe, e passar um objeto que o motor não entende é risco
 * desnecessário. Vide `src/utils/engineVersion.ts`.
 */
const WRITE_TX_OPTIONS: IDBTransactionOptions | undefined =
  typeof IDBTransaction !== 'undefined' &&
  'durability' in IDBTransaction.prototype
    ? { durability: 'relaxed' }
    : undefined;

/** Abre transação de escrita com as opções acima quando suportadas. */
function writeTx(db: IDBDatabase): IDBTransaction {
  return WRITE_TX_OPTIONS
    ? db.transaction(STORE_NAME, 'readwrite', WRITE_TX_OPTIONS)
    : db.transaction(STORE_NAME, 'readwrite');
}

/** Registro persistido: AnalyticsEvent + campo interno de indexação. */
type StoredEvent = AnalyticsEvent & { pending_sync?: number };

class AnalyticsDB implements IAnalyticsDB {
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Gets or creates the IndexedDB database connection.
   */
  private async getDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = this.abrirDB();
    return this.dbPromise;
  }

  private abrirDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const req = event.target as IDBOpenDBRequest;
        const db = req.result;
        const tx = req.transaction; // versionchange transaction (não-null aqui)

        // Create object store if it doesn't exist
        let store: IDBObjectStore;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('synced', 'synced', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('type', 'type', { unique: false });
        } else {
          store = tx!.objectStore(STORE_NAME);
        }

        // v2: índice `pending_sync` (1 = falta sincronizar, 0 = já ack/local).
        // Boolean não é chave válida em IndexedDB → usamos 0/1 numérico.
        //
        // IMPORTANTE: NÃO fazemos backfill dos registros existentes aqui. Um
        // cursor.update() em 165k dentro do onupgradeneeded escreve 165k linhas
        // e trava `getDB()` por MINUTOS no A7 (a Promise só resolve no onsuccess,
        // que espera a transação de upgrade terminar) → apagão de analytics.
        // Só criamos o índice (instantâneo). Registros antigos ficam sem o campo
        // (invisíveis ao índice) → o synced antigo NÃO aparece em getUnsyncedEvents
        // (correto, já foi enviado). O backfill dos NÃO-sincronizados + a limpeza
        // do backlog rodam em `runBackgroundMaintenance()` (chunked, fora do path
        // de playback). Eventos NOVOS já nascem com pending_sync via addEvent.
        if (!store.indexNames.contains('pending_sync')) {
          store.createIndex('pending_sync', 'pending_sync', { unique: false });
        }
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('[AnalyticsDB] Failed to open database:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Adds an event to the database.
   */
  async addEvent(event: AnalyticsEvent): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = writeTx(db);
      const store = tx.objectStore(STORE_NAME);
      // pending_sync = 1 enquanto não sincronizado; vira 0 no markAsSynced.
      const stored: StoredEvent = {
        ...event,
        pending_sync: event.synced ? 0 : 1,
      };
      const request = store.add(stored);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('[AnalyticsDB] Failed to add event:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Gets events that haven't been synced yet.
   */
  async getUnsyncedEvents(limit = 100): Promise<AnalyticsEvent[]> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);

      // Cursor do OBJECT STORE, não do índice `pending_sync`. Vide o cabeçalho
      // do arquivo: o índice era a causa raiz da latência de segundos.
      //
      // Correto porque o store só guarda pendente: desde a 1.0.429 o evento é
      // DELETADO após o ack (deleteSynced), não marcado. O filtro abaixo existe
      // só para registros vestigiais com pending_sync=0 — sincronizados numa
      // versão antiga e ainda não removidos pela manutenção one-shot.
      const pendentes: AnalyticsEvent[] = [];
      let varridos = 0;
      const request = store.openCursor();

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor || pendentes.length >= limit || varridos >= SCAN_CAP) {
          if (varridos >= SCAN_CAP && pendentes.length < limit) {
            // Só acontece em device com muito registro já sincronizado à frente
            // na ordem de chave. A manutenção one-shot os remove; até lá, este
            // teto evita varredura longa no caminho quente.
            logger.debug(
              `[AnalyticsDB] getUnsyncedEvents: teto de varredura (${SCAN_CAP}) com ${pendentes.length} pendentes`,
            );
          }
          resolve(pendentes);
          return;
        }
        varridos += 1;
        const { pending_sync: ps, ...evento } = cursor.value as StoredEvent;
        if (ps !== 0) {
          pendentes.push(evento);
        }
        cursor.continue();
      };

      request.onerror = () => {
        console.error(
          '[AnalyticsDB] Failed to get unsynced events:',
          request.error,
        );
        reject(request.error);
      };
    });
  }

  /**
   * Aplica o teto de acúmulo. Vide MAX_STORED_EVENTS.
   *
   * Se o total está dentro do teto, retorna sem abrir transação de escrita —
   * que é o caso normal, e por isso o caminho barato vem primeiro.
   *
   * Acima do teto, varre o índice `timestamp` em ordem CRESCENTE (mais antigo
   * primeiro) e apaga exatamente o excedente. Como o índice é ordenado, a
   * varredura para assim que couber: o custo é proporcional ao que sai, não
   * ao tamanho da base.
   *
   * Este apaga PENDENTE também — é o único lugar do player que faz isso.
   */
  async enforceRetentionLimits(maxEvents = MAX_STORED_EVENTS): Promise<{
    deleted: number;
    total: number;
    oldest: string | null;
    newest: string | null;
  }> {
    const db = await this.getDB();

    const total = await new Promise<number>((resolve, reject) => {
      const req = db
        .transaction(STORE_NAME, 'readonly')
        .objectStore(STORE_NAME)
        .count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });

    const excedente = Math.max(0, total - maxEvents);
    if (excedente === 0) {
      return { deleted: 0, total, oldest: null, newest: null };
    }
    let restam = excedente;

    return new Promise((resolve, reject) => {
      const tx = writeTx(db);
      const request = tx
        .objectStore(STORE_NAME)
        .index('timestamp')
        .openCursor(); // crescente = mais antigo primeiro

      let deleted = 0;
      let scanned = 0;
      let oldest: string | null = null;
      let newest: string | null = null;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (!cursor || scanned >= RETENTION_SCAN_CAP) return;
        scanned++;

        if (restam === 0) return; // já coube no teto — encerra a varredura

        const ts = (cursor.value as AnalyticsEvent).timestamp;
        cursor.delete();
        deleted++;
        restam--;
        if (oldest === null) oldest = ts; // cursor é crescente
        newest = ts;
        cursor.continue();
      };

      tx.oncomplete = () => {
        if (deleted > 0) {
          console.warn(
            `[AnalyticsDB] Retenção: ${deleted} eventos descartados — acúmulo passou do teto de ${maxEvents}.`,
          );
        }
        resolve({ deleted, total: total - deleted, oldest, newest });
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Métricas de saúde do banco, pro `health:report` (Renato 2026-09-20).
   *
   * As duas que denunciaram a RD-Raia: quantos registros há, e quanto custa
   * uma leitura. Lá eram 210.404 registros e 9.550 ms, num player que
   * entregava os eventos em dia — nenhum indicador do servidor via isso.
   *
   * CUIDADO DELIBERADO com o custo: NÃO conta pendentes pelo índice.
   * `index.count(only(1))` é exatamente a operação que ocupava ~60 s da
   * thread de IndexedDB no Tab A7 e que tiramos do caminho do flush. Como o
   * `deleteSynced` apaga no ack, o total do store já É praticamente o número
   * de pendentes — `store.count()` responde em 2 ms num banco saudável.
   *
   * A latência é medida com uma leitura de UM registro no mesmo índice que o
   * flush usa: mesma forma da consulta real, custo desprezível.
   */
  async getHealthMetrics(): Promise<{
    records: number;
    readMs: number;
    loopLagMs: number;
  }> {
    const db = await this.getDB();

    const records = await new Promise<number>((resolve, reject) => {
      const req = db
        .transaction(STORE_NAME, 'readonly')
        .objectStore(STORE_NAME)
        .count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });

    // Mede o MESMO caminho do flush (cursor do object store), não o índice.
    // Medir o índice reportava a latência de uma query que a produção não faz
    // mais — vide o cabeçalho do arquivo.
    //
    // `performance.now()` em vez de `Date.now()`: resolução de µs e monotônico
    // (imune a ajuste de relógio). Ainda inclui a espera pelo callback na main
    // thread — por isso `loopLagMs` abaixo, que mede SÓ essa espera, medida em
    // paralelo. readMs alto com lag baixo = banco; os dois altos = main thread.
    const agendadoEm = performance.now();
    let lagMs = 0;
    const sondaLag = new Promise<void>((resolve) => {
      setTimeout(() => {
        lagMs = performance.now() - agendadoEm;
        resolve();
      }, 0);
    });

    const t0 = performance.now();
    const leitura = new Promise<void>((resolve, reject) => {
      const req = db
        .transaction(STORE_NAME, 'readonly')
        .objectStore(STORE_NAME)
        .openCursor();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
    await leitura;
    const readMs = Math.round(performance.now() - t0);
    await sondaLag;

    return { records, readMs, loopLagMs: Math.round(lagMs) };
  }

  /**
   * Apaga eventos que o servidor já confirmou (Renato 2026-09-20).
   *
   * Substitui o `markAsSynced` no caminho de sucesso do flush. Depois de
   * sincronizado o registro não serve mais pra nada: NINGUÉM lê evento com
   * `synced=true` — a flag só existia pra decidir o que a purga apagaria
   * DEPOIS. E a resposta do servidor não traz falha por evento (só
   * `received`/`status`), então não há nada a recuperar do registro guardado.
   *
   * Guardar era duas vezes pior: `markAsSynced` faz get+put (2 ops por id)
   * contra 1 delete aqui, e ainda deixava o registro ocupando a base até a
   * purga por idade — no Tab A7 eram ~29 mil registros mortos inflando um
   * IndexedDB de 433 MB pra 26 MB de dado real.
   *
   * A regra de retenção NÃO muda: pendente (não sincronizado) nunca é apagado
   * aqui. Só sai o que o servidor confirmou.
   */
  async deleteSynced(ids: string[]): Promise<void> {
    if (ids.length === 0) return;

    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = writeTx(db);
      const store = tx.objectStore(STORE_NAME);

      ids.forEach((id) => store.delete(id));

      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        console.error('[AnalyticsDB] Falha ao apagar sincronizados:', tx.error);
        reject(tx.error);
      };
    });
  }

  /**
   * Marks events as synced.
   *
   * Mantido pro legado e pros testes; o flush usa {@link deleteSynced}.
   */
  async markAsSynced(ids: string[]): Promise<void> {
    if (ids.length === 0) return;

    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = writeTx(db);
      const store = tx.objectStore(STORE_NAME);

      // Counter intencional pra debug/futuro — não é lido. Prefix `_` evita lint.
      let _completed = 0;
      let hasError = false;

      ids.forEach((id) => {
        const getRequest = store.get(id);

        getRequest.onsuccess = () => {
          const event = getRequest.result as StoredEvent | undefined;
          if (event) {
            event.synced = true;
            event.pending_sync = 0; // sai da fila de pendentes (índice)
            store.put(event);
          }
          _completed++;
        };

        getRequest.onerror = () => {
          hasError = true;
          _completed++;
        };
      });

      tx.oncomplete = () => {
        if (hasError) {
          console.warn('[AnalyticsDB] Some events failed to mark as synced');
        }
        resolve();
      };

      tx.onerror = () => {
        console.error('[AnalyticsDB] Transaction failed:', tx.error);
        reject(tx.error);
      };
    });
  }

  /**
   * Manutenção one-time em background (chunked + yields) — NÃO bloqueia playback.
   *
   * Substitui o backfill que ficava no onupgradeneeded (que travava por minutos).
   * Roda 1× (guardado por flag em localStorage) após o app subir:
   *  - registro NÃO-sincronizado sem pending_sync → seta pending_sync=1 (pra
   *    getUnsyncedEvents/flush acharem e ENVIAREM — preserva billing antigo).
   *  - registro sincronizado mais velho que a retenção → DELETA (limpa o backlog
   *    acumulado, ex.: 165k). Sincronizado recente → deixa como está.
   * Idempotente: se o app morrer no meio, o próximo boot recomeça do zero sem
   * perder nada (novos eventos já persistem na hora via addEvent).
   *
   * @param onProgress callback opcional (deleted, backfilled) por chunk — testes
   */
  async runBackgroundMaintenance(
    onProgress?: (deleted: number, backfilled: number) => void,
  ): Promise<{ deleted: number; backfilled: number }> {
    try {
      if (localStorage.getItem(MAINT_FLAG_KEY) === 'done') {
        return { deleted: 0, backfilled: 0 };
      }
    } catch {
      // localStorage indisponível → segue sem flag (roda toda vez, tolerável).
    }

    let lastKey: IDBValidKey | undefined;
    let totalDeleted = 0;
    let totalBackfilled = 0;

    // eslint-disable-next-line no-constant-condition
    for (;;) {
      const res = await this.maintenanceChunk(lastKey, MAINT_CHUNK);
      totalDeleted += res.deleted;
      totalBackfilled += res.backfilled;
      if (res.deleted || res.backfilled)
        onProgress?.(res.deleted, res.backfilled);
      if (res.scanned === 0) break; // fim do store
      lastKey = res.lastKey;
      // Cede a main-thread entre chunks — não compete com o playback.
      await new Promise((r) => setTimeout(r, 50));
    }

    try {
      localStorage.setItem(MAINT_FLAG_KEY, 'done');
    } catch {
      /* noop */
    }
    if (totalDeleted || totalBackfilled) {
      console.log(
        `[AnalyticsDB] Manutenção v2 concluída: ${totalDeleted} deletados, ${totalBackfilled} backfilled`,
      );
    }
    return { deleted: totalDeleted, backfilled: totalBackfilled };
  }

  /**
   * Processa um chunk da manutenção numa única transação readwrite. Cursora o
   * store por primary key a partir de `afterKey` (resume entre chunks).
   */
  private maintenanceChunk(
    afterKey: IDBValidKey | undefined,
    limit: number,
  ): Promise<{
    scanned: number;
    deleted: number;
    backfilled: number;
    lastKey?: IDBValidKey;
  }> {
    return this.getDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const tx = writeTx(db);
          const store = tx.objectStore(STORE_NAME);
          const range =
            afterKey !== undefined
              ? IDBKeyRange.lowerBound(afterKey, true)
              : undefined;
          const request = store.openCursor(range);

          let scanned = 0;
          let deleted = 0;
          let backfilled = 0;
          let lastKey: IDBValidKey | undefined;

          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
              .result;
            if (!cursor || scanned >= limit) {
              return; // tx.oncomplete resolve
            }
            const rec = cursor.value as StoredEvent;
            lastKey = cursor.primaryKey;
            scanned++;

            if (rec.synced) {
              // Já confirmado pelo servidor → não serve mais pra nada.
              // Sem janela de tempo: vide MAINT_CHUNK.
              cursor.delete();
              deleted++;
            } else if (rec.pending_sync !== 1) {
              // Não-sincronizado sem o campo → torna visível ao índice/flush.
              rec.pending_sync = 1;
              cursor.update(rec);
              backfilled++;
            }
            cursor.continue();
          };

          tx.oncomplete = () =>
            resolve({ scanned, deleted, backfilled, lastKey });
          tx.onerror = () => reject(tx.error);
          request.onerror = () => reject(request.error);
        }),
    );
  }

  /**
   * Clears all events from the database.
   */
  /**
   * Esvazia o store. ATENÇÃO: não recupera espaço nem latência — cada
   * remoção é mais uma escrita no LevelDB. Não há como compactar pelo JS.
   */
  async clear(): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = writeTx(db);
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[AnalyticsDB] All events cleared');
        resolve();
      };

      request.onerror = () => {
        console.error('[AnalyticsDB] Failed to clear events:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Gets all events (for debugging).
   */
  async getAllEvents(): Promise<AnalyticsEvent[]> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }
}

// Export singleton instance
const analyticsDB = new AnalyticsDB();
export default analyticsDB;
